import { apiClient } from '@/lib/api-client';
import { tenantNow } from '@/lib/tenant-clock';

// Mirrors services/api/src/combos — list response shape (paise for money fields).

export interface ComboItem {
  id: string;
  product_id: string;
  product_name: string;
  quantity: number;
  /** Individual product price in paise */
  individual_price: number;
}

export interface Combo {
  id: string;
  location_id: string;
  name: string;
  /** Discounted combo price in paise */
  combo_price: number;
  image_url?: string | null;
  is_active: boolean;
  status: string;
  items: ComboItem[];
  /** Sum of individual prices × qty, in paise */
  individual_total: number;
  savings_percentage: number;
  /** Availability window — time-of-day "HH:MM[:SS]" (null = all day) */
  availability_start?: string | null;
  availability_end?: string | null;
  /** Days of week the combo is sold (0=Sun..6=Sat); null/empty = every day */
  availability_days?: number[] | null;
  /** Validity window — calendar dates "YYYY-MM-DD" (null = no date bound) */
  valid_from?: string | null;
  valid_until?: string | null;
}

interface PaginationMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export const combosService = {
  // GET /api/v1/combos
  list(params: { location_id?: string; status?: string } = {}) {
    const qs = new URLSearchParams();
    if (params.location_id) qs.set('location_id', params.location_id);
    if (params.status) qs.set('status', params.status);
    qs.set('limit', '200');
    const s = qs.toString();
    return apiClient<{ data: Combo[]; meta: PaginationMeta }>(`/combos${s ? `?${s}` : ''}`);
  },
};

/**
 * True when the combo is sellable right now. A combo with no window is always
 * available; otherwise it must fall inside the validity date range AND on an
 * allowed weekday AND inside the time-of-day window (handles overnight
 * windows). Deactivation (`is_active`) is enforced server-side; this only
 * gates the schedule window.
 *
 * `now` defaults to the TENANT's wall clock, not the browser's: a combo that
 * runs 15:00-18:00 on weekends means the business's afternoon and the business's
 * calendar day. Reading the till's clock made a Gulf tenant's window open and
 * close 1.5 hours early whenever the device was still set to IST.
 */
export function isComboAvailableNow(c: Combo, now: Date = tenantNow()): boolean {
  // Validity date-range gate (inclusive; compares on local calendar day)
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  if (c.valid_from && today < c.valid_from.slice(0, 10)) return false;
  if (c.valid_until && today > c.valid_until.slice(0, 10)) return false;

  // Day-of-week gate (only when days are specified)
  if (c.availability_days && c.availability_days.length > 0 && !c.availability_days.includes(now.getDay())) {
    return false;
  }
  // Time-of-day gate (only when both bounds are set)
  if (c.availability_start && c.availability_end) {
    const toMin = (t: string) => {
      const [h, m] = t.slice(0, 5).split(':').map(Number);
      return h * 60 + m;
    };
    const cur = now.getHours() * 60 + now.getMinutes();
    const start = toMin(c.availability_start);
    const end = toMin(c.availability_end);
    const within = start <= end ? cur >= start && cur <= end : cur >= start || cur <= end;
    if (!within) return false;
  }
  return true;
}
